import { ReactElement, useCallback, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import { useHistory } from 'react-router-dom';
import cc from 'classcat';
import {
  calculateBonus,
  calculateCasinoBalance
} from 'utils/account/calculate-balance';
import { IS_ACTIVE_TWO_WALLET } from 'utils/constants/account/payment';
import { EIconSize } from 'utils/constants/app/ui';
import SpringConfigs from 'utils/constants/swarm/spring-configs';
import { thousandSeparator } from 'utils/helpers/validators';
import { isMobile } from 'utils/is-mobile';
import { GlobalIcon } from 'components/GlobalIcon';
import { updateQuery } from 'components/QueryRoute';
import { Button } from 'newcomponents/UI/Button';
import { Skeleton } from 'newcomponents/UI/Skeleton';
import { useCurrency } from 'hooks/useCurrency';
import { useSearchParams } from 'hooks/useSearchParams';
import { useFetchAccountParams } from 'newhooks/account/useFetchAccountParams';
import { getUser } from 'store/selectors/user-data';
import './index.less';

type Props = {
  onClick?: () => void;
  children?: ReactElement;
};

export const AccountsBalance = (props: Props): ReactElement => {
  const { t } = useTranslation();
  const history = useHistory();
  const { wallet } = useSearchParams();
  const { accountParams } = useFetchAccountParams();
  const user = useSelector(getUser);
  const { currency, currencyId, placement } = useCurrency();

  const [isMainBalanceExpanded, setIsMainBalanceExpanded] = useState(false);

  const [isBonusBalanceExpanded, setIsBonusBalanceExpanded] = useState(
    !!SpringConfigs.IS_BONUS_BALANCE_VIEW_EXPANDED_BY_DEFAULT
  );

  const BalanceSkeleton = useCallback(() => {
    return (
      <div className="accountBalance__valueCurrency">
        <Skeleton
          title={{
            style: { width: 36, height: 26, margin: 0 }
          }}
          paragraph={false}
          active
        />
      </div>
    );
  }, []);

  const casinoBalance = useMemo(() => {
    return IS_ACTIVE_TWO_WALLET ? (
      <div
        className={cc([
          'accountBalance__balance',
          'accountBalance__balance__bottom'
        ])}
      >
        <div className="accountBalance__label">
          {t('account.casinoBalance')}
        </div>
        {currencyId ? (
          <div
            className="accountBalance__valueCurrency"
            data-testid="casino-balance"
          >
            {placement === 'left' && (
              <div className="accountBalance__currency accountBalance__currency-left">
                {currency}
              </div>
            )}
            <div
              className="accountBalance__value"
              data-testid="casino-balance"
              dir="ltr"
            >
              {calculateCasinoBalance(user.casino_balance)}
            </div>
            {placement === 'right' && (
              <div className="accountBalance__currency accountBalance__currency-right">
                {currency}
              </div>
            )}
          </div>
        ) : (
          <BalanceSkeleton />
        )}
      </div>
    ) : null;
  }, [currencyId, currency, user.casino_balance]);

  const bonusBalances = useMemo(() => {
    return (
      <>
        <div className="accountBalance__balance accountBalance__balance__bottom">
          <div className="accountBalance__label">
            {t('account.frozenBalance')}
          </div>
          {currencyId ? (
            <div className="accountBalance__valueCurrency">
              {placement === 'left' && (
                <div className="accountBalance__currency accountBalance__currency-left">
                  {currency}
                </div>
              )}
              <div
                className="accountBalance__value"
                data-testid="frozen-balance"
              >
                {thousandSeparator(user.frozen_balance)}
              </div>
              {placement === 'right' && (
                <div className="accountBalance__currency accountBalance__currency-right">
                  {currency}
                </div>
              )}
            </div>
          ) : (
            <BalanceSkeleton />
          )}
        </div>

        {!!user.bonus_money && (
          <div className="accountBalance__balance accountBalance__balance__bottom">
            <div className="accountBalance__label">{t('account.freeBet')}</div>
            {currencyId ? (
              <div className="accountBalance__valueCurrency">
                {placement === 'left' && (
                  <div className="accountBalance__currency accountBalance__currency-left">
                    {currency}
                  </div>
                )}
                <div
                  className="accountBalance__value"
                  data-testid="freebet-balance"
                >
                  {thousandSeparator(user.bonus_money)}
                </div>
                {placement === 'right' && (
                  <div className="accountBalance__currency accountBalance__currency-right">
                    {currency}
                  </div>
                )}
              </div>
            ) : (
              <BalanceSkeleton />
            )}
          </div>
        )}
      </>
    );
  }, [currencyId, currency, user.frozen_balance, user.bonus_money]);

  return (
    <div className="accountBalance">
      <div className="accountBalance__wrapper">
        <div
          className={cc([
            'accountBalance__group',
            { 'accountBalance__group--expandable': casinoBalance }
          ])}
          onClick={() => setIsMainBalanceExpanded(prev => !prev)}
        >
          <div className="accountBalance__balance">
            <div className="accountBalance__label">
              {t('account.accountBalance')}
              {casinoBalance ? (
                <GlobalIcon
                  lib="generic"
                  name="caretDown"
                  theme="default"
                  size={EIconSize._11}
                  useWrapper
                  className={cc([
                    'accountBalance__caret',
                    {
                      'accountBalance__caret--expanded': isMainBalanceExpanded
                    }
                  ])}
                />
              ) : null}
            </div>
            {currencyId ? (
              <div className="accountBalance__valueCurrency">
                {placement === 'left' && (
                  <div className="accountBalance__currency accountBalance__currency-left">
                    {currency}
                  </div>
                )}
                <div
                  className="accountBalance__value"
                  data-testid="account-balance"
                  dir="ltr"
                >
                  {thousandSeparator(user.balance)}
                </div>
                {placement === 'right' && (
                  <div className="accountBalance__currency accountBalance__currency-right">
                    {currency}
                  </div>
                )}
              </div>
            ) : (
              <BalanceSkeleton />
            )}
          </div>
          {isMainBalanceExpanded && casinoBalance}
        </div>

        {+(accountParams?.showBonusBalance ?? 1) ? (
          <div
            className={cc([
              'accountBalance__group',
              { 'accountBalance__group--expandable': bonusBalances }
            ])}
            onClick={() => setIsBonusBalanceExpanded(prev => !prev)}
          >
            <div className="accountBalance__balance">
              <div className="accountBalance__label">
                {t('account.bonusBalance')}
                {bonusBalances ? (
                  <GlobalIcon
                    lib="generic"
                    name="caretDown"
                    theme="default"
                    useWrapper
                    size={EIconSize._11}
                    className={cc([
                      'accountBalance__caret',
                      {
                        'accountBalance__caret--expanded':
                          isBonusBalanceExpanded
                      }
                    ])}
                  />
                ) : null}
              </div>
              {currencyId ? (
                <div className="accountBalance__valueCurrency">
                  {placement === 'left' && (
                    <div className="accountBalance__currency accountBalance__currency-left">
                      {currency}
                    </div>
                  )}
                  <div
                    className="accountBalance__value"
                    data-testid="bonus-balance"
                    dir="ltr"
                  >
                    {calculateBonus(user)}
                  </div>
                  {placement === 'right' && (
                    <div className="accountBalance__currency accountBalance__currency-right">
                      {currency}
                    </div>
                  )}
                </div>
              ) : (
                <BalanceSkeleton />
              )}
            </div>
            {isBonusBalanceExpanded && bonusBalances}
          </div>
        ) : null}
      </div>

      {(!wallet || !isMobile()) && (
        <div
          className="accountBalance__btnWrapper"
          onClick={e => {
            e.stopPropagation();
            history.push(
              updateQuery({
                accounts: '*',
                wallet: '*',
                [isMobile() ? 'deposit-methods' : 'deposit']: '*'
              })
            );
          }}
        >
          <Button size="large" fullwidth type="primary">
            <div className="accountBalance__buttonContent">
              <GlobalIcon
                lib="generic"
                name="add"
                theme="default"
                size={EIconSize._16}
                className="accountBalance__addIcon"
              />
              {t('account.deposit')}
            </div>
          </Button>
        </div>
      )}
      {props.children}
    </div>
  );
};
